import {List, Set} from 'immutable';
import {IFilterFilter, IPromise} from 'angular';
import {IStation} from '../../api/api.types';
import {IStationApi} from '../../api/station-api.service';
import {IStationSelectorService} from './selector.service';

export default class StationSelectorController {
  private static readonly $inject = [
    'stationApi',
    '$filter',
    '$element',
    'stationSelectorService'
  ];
  private static readonly STORAGE_KEY = 'selector';

  /**
   * Search text for station selection.
   */
  searchText: string;
  /**
   * Selected station in the autocomplete dropdown.
   */
  selectedStation: IStation;
  /**
   * Stations selected.
   */
  selectedStations: Set<IStation>;
  /**
   * Id of the highlighted station.
   */
  highlightedStationId: number|null;
  /**
   * Array view of the selected stations (for ng-repeat).
   */
  selectedStationsArray: IStation[];
  /**
   * Callback to notify of changes in the set of selected stations.
   */
  onSelectChange: (callbackArgs: {
    selectedStations: Set<IStation>
  }) => void;
  /**
   * Callback to notify of a change in the highlighted station.
   */
  onStationHighlightChange: (callbackArgs: {
    station: IStation
  }) => void;

  /**
   * Promise that resolves to the array of available stations.
   */
  private stationsPromise: IPromise<IStation[]>;
  /**
   * AngularJS filter function.
   */
  private filter: IFilterFilter;

  constructor(private stationApi: IStationApi,
              $filter: ng.IFilterService,
              private $element: ng.IAugmentedJQuery,
              private stationSelectorService: IStationSelectorService) {
    this.filter = $filter('filter');
    this.stationsPromise = stationApi.getMeteoStations().then((stations) => {
      this.selectedStations = this.stationSelectorService.getStoredStations(
          StationSelectorController.STORAGE_KEY, List(stations));
      this.selectedStationsArray = this.selectedStations
          .toArray().sort(compareStations);
      if (!this.selectedStations.isEmpty()) {
        this.highlightedStationId = this.selectedStationsArray[0].id;
      }
      this.notifyChanges();
      return stations;
    });
  }

  onChanges() {
    if (!this.selectedStations.find(
        station => station.id == this.highlightedStationId)) {
      this.highlightedStationId = null;
    }
  }

  /**
   * Input element in the md-autocomplete component.
   */
  private get inputElement(): HTMLInputElement {
    return <HTMLInputElement>this.$element[0].querySelector('#station-input');
  }

  /**
   * Returns the stations matching a search text.
   */
  getMatchedStations(searchText: string) {
    return this.stationsPromise.then(stations => {
      return this.filter(stations, searchText).filter(station => {
        return !this.selectedStations.contains(station);
      });
    });
  }

  /**
   * Add the selected station to the set.
   */
  addToSelectedStations(station: IStation) {
    if (this.highlightedStationId == null) {
      this.highlightedStationId = station.id;
    }
    this.selectedStations = this.selectedStations.add(station);
    this.selectedStationsArray = this.selectedStations
        .toArray().sort(compareStations);
    this.searchText = '';
    this.inputElement && this.inputElement.blur();
    this.notifyChanges();
  }

  /**
   * Remove a selected station from the set.
   */
  removeFromSelectedStations(station: IStation) {
    this.selectedStations = this.selectedStations.remove(station);
    this.selectedStationsArray = this.selectedStations
        .toArray().sort(compareStations);
    if (station.id == this.highlightedStationId) {
      this.highlightedStationId = this.selectedStations.isEmpty()
          ? null
          : this.selectedStationsArray[0].id;
    }
    this.notifyChanges();
  }

  /**
   * Highlight a station in the list.
   */
  highlightStation(station: IStation) {
    this.highlightedStationId = station.id;
    this.notifyChanges();
  }

  onStationRadioChange() {
    this.notifyChanges();
  }

  /**
   * Notify the callback about changes in the set of selected stations and
   * store it locally.
   */
  private notifyChanges() {
    this.stationSelectorService.storeStations(
        StationSelectorController.STORAGE_KEY,
        this.selectedStations);
    if (this.onSelectChange) {
      this.onSelectChange({
        selectedStations: this.selectedStations
      });
    }
    if (this.onStationHighlightChange) {
      this.onStationHighlightChange({
        station: this.selectedStations.find(
            station => station.id == this.highlightedStationId)
      });
    }
  }
}

function compareStations(a: IStation, b: IStation):number {
  return a.siteCode.localeCompare(b.siteCode);
}



/** WEBPACK FOOTER **
 ** ./app/meteostation/selector/selector.controller.ts
 **/